import type { ReactNode } from "react";
import { fmtPct, tidyText } from "../lib/format";
import type { DeltaDirection, DeltaResult } from "../lib/delta";
import { IconArrowUp, IconArrowDown, IconMinus, IconWhatsApp } from "./icons";
import { CardMemoryTrigger } from "./card-memory/CardMemoryTrigger";
import type { CardMemoryRef } from "./card-memory/card-memory";

export type CardStatus = "good" | "warning" | "critical" | "neutral";

interface Props {
  label: string;
  value: string;
  valueSub?: string;
  /** Linha cinza sob o valor (ex.: "88% do custo total"), independente de delta. */
  subtitle?: string;
  delta?: DeltaResult | null;
  comparisonLabel?: string | null;
  comparisonValue?: string;
  status?: CardStatus;
  insight?: string;
  action?: ReactNode;
  onWhatsApp?: () => void;
  loading?: boolean;
  /** memória de cálculo do card: com ela, o card inteiro abre a composição do número */
  memory?: CardMemoryRef | null;
  /** @deprecated Use delta prop instead */
  trend?: "up" | "down" | "flat";
  /** @deprecated Use delta prop instead */
  variationPct?: number | null;
  /** @deprecated Use delta prop instead */
  invertTrend?: boolean;
}

const STATUS_STYLES: Record<CardStatus, { bar: string; badge: string; label: string }> = {
  good:     { bar: "bg-tm-500",      badge: "badge-green", label: "Bom" },
  warning:  { bar: "bg-warning-500", badge: "badge-amber", label: "Atenção" },
  critical: { bar: "bg-danger-600",  badge: "badge-red",   label: "Crítico" },
  neutral:  { bar: "bg-gray-300",    badge: "badge-gray",  label: "—" },
};

function TrendBadge({ delta }: { delta: DeltaResult }) {
  // isGood null (ex.: direction "neutral" do computeDelta) é intencional — o
  // indicador não tem direção boa/ruim, então a cor fica neutra, não inferida
  // do sentido da seta (senão todo "neutral" para baixo pintava de vermelho).
  const color = delta.trend === "flat" || delta.isGood == null ? "text-gray-400"
    : delta.isGood ? "text-tm-600" : "text-danger-600";

  const text = delta.label ?? (delta.pct != null ? fmtPct(Math.abs(delta.pct)) : "—");

  return (
    <span className={`inline-flex items-center gap-0.5 text-sm font-medium ${color}`}>
      {delta.trend === "up"   && <IconArrowUp className="w-4 h-4" />}
      {delta.trend === "down" && <IconArrowDown className="w-4 h-4" />}
      {delta.trend === "flat" && <IconMinus className="w-4 h-4" />}
      {text}
    </span>
  );
}

export function KPIStatCard({
  label, value, valueSub, subtitle, delta, comparisonLabel,
  comparisonValue, status = "neutral", insight, action, onWhatsApp,
  loading = false,
  memory,
  // legacy props
  trend = "flat", variationPct, invertTrend = false,
}: Props) {
  const st = STATUS_STYLES[status];

  // Build a DeltaResult from legacy props when delta is not provided
  // `trend` tem default "flat" por compatibilidade: sem variação, a linha de
  // tendência viraria um "— —" que não informa nada. Só o rótulo de comparação
  // (ex. "checklists abertos") não basta: ele vira texto simples, abaixo.
  const effectiveDelta: DeltaResult | null = delta ?? (
    trend !== undefined && variationPct != null
      ? {
          pct: variationPct ?? null,
          label: null,
          trend,
          isGood: trend === "flat" ? null : invertTrend ? trend === "down" : trend === "up",
        }
      : null
  );

  if (loading) {
    return (
      <div className="card p-5 space-y-3 animate-pulse">
        <div className="h-3 w-24 bg-gray-200 rounded" />
        <div className="h-8 w-32 bg-gray-200 rounded" />
        <div className="h-3 w-20 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <CardMemoryTrigger memory={memory ? { title: label, ...memory } : null}>
    <div className="card overflow-hidden hover:shadow-card-md transition-shadow h-full">
      {/* Status bar */}
      <div className={`h-1 ${st.bar}`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 mb-1">
          <p className="min-w-0 text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
          {status !== "neutral" && <span className={`${st.badge} flex-shrink-0`}>{st.label}</span>}
        </div>

        {/* Value */}
        {/* break-words: em grids de muitas colunas (ex. 6 cards lado a lado), um
            valor longo (ex. "R$ 1.226.703,85") não tem espaço pra quebrar e o
            card (overflow-hidden) cortava o final do número sem indicar nada.
            Quebrar o valor em 2 linhas é sempre melhor que esconder um dígito. */}
        <div className="mt-2 break-words">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {valueSub && <span className="ml-2 text-sm text-gray-400">{valueSub}</span>}
        </div>

        {/* Subtitle (linha cinza independente de delta) */}
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        {!subtitle && !effectiveDelta && comparisonLabel && (
          <p className="text-xs text-gray-400 mt-1">{tidyText(comparisonLabel)}</p>
        )}

        {/* Trend */}
        {effectiveDelta && (
          <div className="mt-2 flex items-center gap-2">
            <TrendBadge delta={effectiveDelta} />
            {comparisonLabel && <span className="text-xs text-gray-400">{tidyText(comparisonLabel)}</span>}
          </div>
        )}

        {/* Comparison value */}
        {comparisonValue && (
          <p className="text-xs text-gray-400 mt-0.5">vs. {comparisonValue}</p>
        )}

        {/* Insight */}
        {insight && (
          <p className="mt-3 text-xs text-gray-500 bg-surface-muted px-3 py-2 rounded-lg border-l-2 border-tm-300 leading-relaxed">
            {insight}
          </p>
        )}

        {/* Actions */}
        {(action || onWhatsApp) && (
          // Ação do card não abre a memória.
          <div className="mt-4 flex items-center gap-2 pt-3 border-t border-surface-border" onClick={(e) => e.stopPropagation()}>
            {action}
            {onWhatsApp && (
              <button
                onClick={onWhatsApp}
                className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-tm-600 transition-colors"
              >
                <IconWhatsApp className="w-3.5 h-3.5" />
                Perguntar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </CardMemoryTrigger>
  );
}
